import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import axios from "axios";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingIcon } from "src/components/icons";
import ReactTable from "src/components/reusable/ReactTable";
import { API_PROD_URL } from "src/utils/axios";

interface EmailLog {
  id: number;
  to_email: string;
  subject: string;
  status: string;
  error_message: string | null;
  sent_at: string;
}

const EmailLogs: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // campaign_id
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    const fetchLogs = async () => {
      setEmailLoading(true);
      try {
        const response = await axios.get(`${API_PROD_URL}/email/logs`, {
          params: {
            campaignId: id,
            page: 1,
            limit: 100,
          },
        });
        setLogs(response.data.logs || []);
        setTotal(response.data.logs.length || 0);
      } catch (error) {
        console.error("Failed to fetch email logs:", error);
      } finally {
        setEmailLoading(false);
      }
    };

    fetchLogs();
  }, [id]);

  const columnHelper = createColumnHelper<EmailLog>();
  const columns: ColumnDef<EmailLog, any>[] = [
    columnHelper.accessor("to_email", {
      header: "Recipient Email",
      cell: (info) => (
        <span className="font-medium text-gray-800">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("subject", {
      header: "Subject",
      cell: (info) => (
        <span className="text-gray-700 line-clamp-1">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        const color =
          status === "success"
            ? "text-green-600 bg-green-50"
            : status === "opened"
            ? "text-blue-600 bg-blue-50"
            : "text-red-600 bg-red-50";
        return (
          <span
            className={`px-2 py-1 rounded-md text-sm font-medium ${color}`}
          >
            {status}
          </span>
        );
      },
    }),
    columnHelper.accessor("sent_at", {
      header: "Sent At",
      cell: (info:any) =>
        new Date(info.getValue()).toLocaleString(undefined, {
          dateStyle: "short",
          timeStyle: "short",
        }),
    }),
    columnHelper.accessor("error_message", {
      header: "Error Message",
      cell: (info:any) =>
        info.getValue() ? (
          <span className="text-red-500 text-sm">{info.getValue()}</span>
        ) : (
          <span className="text-gray-400 text-sm">—</span>
        ),
    }),
  ];

  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div className="bg-white rounded-2xl shadow-sm p-0 border-2 border-gray-200 mt-4">
      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="w-full flex items-center justify-between p-3"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium">Email Logs</h2>
          <span className="text-sm text-gray-500">Total: {total}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-600 transition-transform ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>

      <div
        className={`overflow-hidden transition-[max-height] duration-300 px-3 ${expanded ? "max-h-[1000px] py-3" : "max-h-0"
          }`}
      >
        {emailLoading ? (
            <LoadingIcon fontSize={52} className="animate-spin text-primary-900" />
        ) : logs.length === 0 ? (
          <p className="text-gray-500 text-center">No email logs found</p>
        ) : (
          <ReactTable columnsData={columns} rowsData={logs} size="medium" noTopBorder />
        )}
      </div>
    </div>
  );
};

export default EmailLogs;